import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import { t } from "i18next";
import { usePagination } from "../../../hooks/usePagination";
import CustomTable from "../../../components/CustomTable/CustomTable";
import PaginationHandler from "../../../components/PaginationHandler";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { getStatusDeals, deleteStatusDeal } from "../../../api/statusDeals";

const LEVEL_COLORS = {
  start: "primary",
  failed: "danger",
  final: "success",
};

export default function StatusDealList() {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("currentUser")) || {};
  const role = currentUser.role || currentUser.roleName || {};

  const hasPermission = (action) => {
    if (role.name === "super admin") return true;
    return (
      role.permissions &&
      role.permissions.some(
        (perm) => perm.resource === "Sales" && perm.actions.includes(action)
      )
    );
  };

  const canCreate = () => hasPermission("create");
  const canEdit = () => hasPermission("update");
  const canDelete = () => hasPermission("delete");
  const canRead = () =>
    hasPermission("read") || hasPermission("delete") || hasPermission("update");

  const { response, loading, error, setPage, page, totalPages } = usePagination(
    getStatusDeals,
    {},
    true
  );

  const handleDelete = async (id) => {
    if (window.confirm(t("Are you sure you want to delete this status?"))) {
      try {
        await deleteStatusDeal(id);
        setPage(1);
      } catch (error) {
        console.error("Delete failed:", error);
        alert(t("Failed to delete status."));
      }
    }
  };

  if (!canRead()) {
    return <div>{t("You do not have permission to view status deals.")}</div>;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title={t("Status Deals")}
          addUrl={canCreate() ? "/status-deals/add" : null}
          hasAddBtn={canCreate()}
          isMainPage={true}
        />

        {response && response.length > 0 && !loading ? (
          <>
            <CustomTable
              emptyMsg={t("No Status Deals Found.")}
              data={response}
              loading={loading}
              error={error}
              cols={[
                {
                  label: t("Name"),
                  key: "name",
                  style: { width: "40%" },
                },
                {
                  label: t("Level"),
                  key: "level",
                  style: { width: "40%" },
                  renderer: (row) => {
                    const color = LEVEL_COLORS[row.level] || "secondary";
                    return (
                      <div className="d-flex align-items-center gap-2">
                        <span className={`badge bg-${color} text-capitalize`}>
                          <span className="me-1">★</span>
                          {t(row.level)}
                        </span>
                      </div>
                    );
                  },
                },
                {
                  label: t("Actions"),
                  key: "actions",
                  style: { width: "20%" },
                  renderer: (row) => (
                    <div className="d-flex gap-2">
                      {canEdit() && (
                        <Button
                          color="info"
                          size="sm"
                          onClick={() =>
                            navigate(`/status-deals/edit/${row._id}`)
                          }
                        >
                          {t("Edit")}
                        </Button>
                      )}
                      {canDelete() && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDelete(row._id)}
                        >
                          {t("Delete")}
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
            />

            <PaginationHandler
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          </>
        ) : (
          !loading && <div>{t("No results found.")}</div>
        )}
      </div>
    </div>
  );
}
